import React, { useContext } from "react";
import { observer } from "mobx-react-lite";

import { BasicTable, LinkButton, RegularText, TextTableHeader } from "shared/components";
import pl from "shared/messages/pl";
import { constants } from "shared/constants";
import { OfwcaProfile } from "view360Card/models/OfwcaProfile";
import { MobileViewCard } from "../mobileViewCard/MobileViewCard";
import { MobileViewCardDetailsItem } from "../mobileViewCardDetailsItem/MobileViewCardDetailsItem";
import styles from "./ofwcaProfilesTable.module.scss";
import { useNavigate } from "react-router-dom";
import { getLinkToEntity } from "shared/routing/pages";
import { SearchEntityTypeEnum, UserBasicEntity } from "view360Card/models";
import { AadUserDetails } from "modules/aad-user-details";
import { MsTeamsContext } from "msTeams/store";
import { Flex } from "@fluentui/react-northstar";
import { ProfileIdentity } from "view360Card/models/ProfileIdentity";

export interface ProfilesTableProps {
    profiles: OfwcaProfile[];
}

export const OfwcaProfilesTable: React.FC<ProfilesTableProps> = observer((props) => {
    const navigation = useNavigate();
    const msTeamsContext = useContext(MsTeamsContext);

    const headerItems = [
        {
            content: (
                <TextTableHeader
                    content={`${pl.view360Card.generalInformations.salesPerson.ofwcaProfile.name} ${constants.inlineSeparator} ${pl.view360Card.generalInformations.salesPerson.ofwcaProfile.number}`}
                />
            ),
            key: "name",
            className: styles.profileName,
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.profileNumberList} />,
            key: "profileNumberList",
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.ofwcaProfile.role} />,
            key: "role",
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.pd} />,
            key: "pd",
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.rms} />,
            key: "rms",
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.channel} />,
            key: "channel",
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.ofwcaProfile.access} />,
            key: "access",
        },
    ];

    const getIntermediaryLink = (profile: OfwcaProfile) => {
        const text = `${profile.name ?? constants.noValue} ${constants.inlineSeparator} ${
            profile.number ?? constants.noValue
        }`;

        if (profile.intermediaryId) {
            return (
                <LinkButton
                    text={text}
                    onClick={() => {
                        navigation(getLinkToEntity(SearchEntityTypeEnum.Intermediary, profile.intermediaryId ?? 0));
                    }}
                />
            );
        }

        return <RegularText content={text} />;
    };

    const getProfileIdentityList = (profileIdentityList: ProfileIdentity[]) => {
        return profileIdentityList && profileIdentityList.length > 0
            ? <Flex gap="gap.smaller" wrap>
                {profileIdentityList.map((p, index) => {
                    return <Flex key={index}>
                        <RegularText
                            content={p.number}
                            weight={p.isMain ? "semibold" : "regular"}
                        />
                        {index + 1 !== profileIdentityList.length && <RegularText content=","/>}
                    </Flex>
                })}
            </Flex>
            : <RegularText content={constants.noValue} />
    };

    const getRows = () => {
        return props.profiles.map((profile) => {
            return {
                key: profile.number,
                items: [
                    {
                        key: `${profile.number}-name`,
                        content: getIntermediaryLink(profile),
                        className: styles.profileName,
                    },
                    {
                        key: `${profile.number}-profileNumberList`,
                        content: getProfileIdentityList(profile.profileIdentityList),
                    },
                    {
                        key: `${profile.number}-role`,
                        content: <RegularText content={profile.role ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.number}-pd`,
                        content: <RegularText content={profile.branch ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.number}-rms`,
                        content: profile.rms ? (
                            <AadUserDetails
                                fullName={profile.rms?.fullName}
                                email={profile.rms?.userPrincipalName}
                                phoneNumber={profile.rms?.mobilePhone}
                                mobileClient={msTeamsContext.isMobileClient}
                            />
                        ) : (
                            <RegularText content={constants.noValue} />
                        ),
                    },
                    {
                        key: `${profile.number}-channel`,
                        content: <RegularText content={profile.channel ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.number}-access`,
                        content: <RegularText content={profile.systemAccess ?? constants.noValue} />,
                    },
                ],
            };
        });
    };

    const getRmsData = (rms: UserBasicEntity | undefined) => {
        if (!rms) {
            return constants.noValue;
        }

        return (
            <AadUserDetails
                fullName={rms?.fullName}
                email={rms?.userPrincipalName}
                phoneNumber={rms?.mobilePhone}
                mobileClient={msTeamsContext.isMobileClient}
            />
        );
    };

    const getMobileHeader = (profile: OfwcaProfile) => {
        return `${profile.name} ${constants.inlineSeparator} ${profile.number}`;
    };

    const getMobilViewDetailItem = (profile: OfwcaProfile) => {
        return [
            <MobileViewCardDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.ofwcaProfile.role}
                content={profile.role}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.profilesTable.pd}`}
                content={`${profile.branch ?? constants.noValue}`}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.profilesTable.rms}`}
                content={getRmsData(profile.rms)}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.profilesTable.channel}`}
                content={`${profile.channel}`}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.generalInformations.salesPerson.ofwcaProfile.access}`}
                content={`${profile.systemAccess}`}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.profilesTable.profileNumberList}`}
                content={getProfileIdentityList(profile.profileIdentityList)}
            />,
        ];
    };

    const getMobileViewRows = () => {
        if (!props.profiles) {
            return null;
        }

        return props.profiles.map((profile) => {
            return (
                <MobileViewCard
                    key={profile.number}
                    header={getMobileHeader(profile)}
                    detailsItems={getMobilViewDetailItem(profile)}
                    headerIsLink={true}
                    headerOnClick={() => {
                        navigation(getLinkToEntity(SearchEntityTypeEnum.Intermediary, profile.intermediaryId ?? 0));
                    }}
                />
            );
        });
    };

    return (
        <BasicTable
            rows={getRows()}
            headerItems={headerItems}
            mobileViewRows={getMobileViewRows()}
            maxDefaultSize={3}
        />
    );
});
